import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { startCase } from 'lodash-es';
import { CostCenter, Product } from '@portal/portal-core';
import { compareDates, compareNames } from 'utils';
import FjSortTable from 'components/table/fj-sort-table';
import TableCheckbox from 'components/table/table-checkbox';
import { NameCell, CheckboxCell, DateTimeCell } from 'components/table/table-cells';
import { Button } from 'components/styled-primitives';

import { selectActiveLicenseRows, selectLicenseRequestProgress } from 'selectors/super-admin';
import { SuperAdminActions, CloudAdminActions } from 'actions';
import { MODAL_TYPES } from 'constants/super-admin';
import { useAppDispatch } from 'store/hooks';

// Minimal shape of a license row as accessed by the column descriptors below.
// Declared as a `type` (not `interface`) so it carries an implicit index
// signature and remains assignable to FjSortTable's `Record<string, unknown>`
// row/column contracts.
type LicenseFeature = { enabled?: boolean; name: string };

type LicenseRow = {
  admin: { primaryEmail: string; firstname: string; lastname: string; phantom?: boolean };
  productAccess: {
    id: string;
    name: string;
    status?: string;
    type?: string;
    activityState?: string;
    startDate?: string;
    endDate?: string;
    features?: Record<string, LicenseFeature>;
  };
  costCenter: { id: string; countryCode?: string };
};

interface CellProps {
  path: string;
  original: LicenseRow;
}

interface NameCellValue {
  id: string;
  email: string;
  firstname: string;
  lastname: string;
  phantom?: boolean;
}

interface IndividualLicenseTableProps {
  licenses: LicenseRow[];
  filterControls?: React.ReactNode;
  controls?: React.ReactNode;
  syncFailed?: boolean;
  loading?: boolean;
  percentLoaded?: number;
  currentRequest?: number;
  requestsCount?: number;
  nextRequest?: number;
  dismissed?: boolean;
  onRetrySync?: () => void;
  onDismissSyncPrompt?: () => void;
  // See institutional-license-table.tsx for the identical rationale: hides FjSortTable's own
  // (redundant, once server pagination already governs the row set) Rows/Page chrome, and
  // defaultPageSize must stay large enough that FjSortTable's still-active client pagination
  // never truncates rows now that there's no control left to page to them.
  hidePagination?: boolean;
  defaultPageSize?: number;
}

const PaddedTable = styled(FjSortTable)`
  padding-left: ${({ theme }) => theme.grid.units(1)};
`;

const TableContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  /* A flex item defaults to min-width: auto, which refuses to shrink below the
     content's own width. Without this the table can never scroll horizontally —
     it just grows past its container and gets clipped by whatever is upstream. */
  min-width: 0;
  min-height: 360px;
  height: 100%;
`;

const SyncFailedOverlay = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  margin: 12px 0 0;
  background: rgba(142, 174, 178, 0.12);
  border: 1px solid rgba(35, 31, 32, 0.12);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  box-sizing: border-box;
`;

const SyncFailedContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  max-width: 720px;
  padding: 16px;
  text-align: center;

  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.selectableLightBg};
    line-height: 1.4;
  }

  button + button {
    margin-left: 0;
  }
`;

const IndividualLicenseTable = ({
  licenses,
  filterControls,
  controls,
  syncFailed = false,
  loading = false,
  percentLoaded,
  currentRequest = 0,
  requestsCount = 0,
  nextRequest = 1,
  dismissed = false,
  onRetrySync,
  onDismissSyncPrompt,
  hidePagination = false,
  defaultPageSize,
}: IndividualLicenseTableProps): React.ReactElement => {
  const selectedRows = useSelector(selectActiveLicenseRows);
  const requestProgress = useSelector(selectLicenseRequestProgress);

  const dispatch = useAppDispatch();
  const selectRow = (licenseId: string, costCenterId: string) =>
    dispatch(SuperAdminActions.selectLicenseRow(licenseId, costCenterId));
  const deselectRow = (id: string) => dispatch(SuperAdminActions.deselectLicenseRow(id));
  const openLicenseDetailsModal = (modalType: string, id: string) => {
    dispatch(CloudAdminActions.showModal(modalType));
    dispatch(SuperAdminActions.selectLicenseDetails(id));
  };

  const getColumns = (): Record<string, unknown>[] => {
    const defaults = {
      cellProps: ({
        path,
        original: {
          admin: { primaryEmail: email },
          productAccess: { name: product },
        },
      }: CellProps) => ({
        dataTestId: `${path}__${email}__${product.toLowerCase()}`,
      }),
    };
    return [
      {
        id: 'select',
        path: 'productAccess.id',
        Cell: ({
          value,
          original: {
            costCenter: { id: costCenterId },
          },
        }: {
          value: string;
          original: LicenseRow;
        }) => (
          <TableCheckbox
            rowId={value}
            selectRow={() => selectRow(value, costCenterId)}
            deselectRow={deselectRow}
            selectedRows={selectedRows}
          />
        ),
        CellContainer: CheckboxCell,
        width: 40,
        resizable: false,
        sortable: false,
        ...defaults,
      },
      {
        id: 'name',
        path: ({
          admin: { primaryEmail: email, firstname, lastname, phantom },
          productAccess: { id },
        }: LicenseRow): NameCellValue => ({
          email,
          firstname,
          lastname,
          phantom,
          id,
        }),
        Header: () => 'Name',
        Cell: ({ value: { id, ...value } }: { value: NameCellValue }, props: Record<string, unknown>) => (
          <NameCell
            onClick={() => openLicenseDetailsModal(MODAL_TYPES.LICENSE_DETAILS, id)}
            {...value}
            {...props}
          />
        ),
        CellContainer: Fragment,
        sortMethod: compareNames,
        alignLeft: true,
        minWidth: 130,
        ...defaults,
      },
      {
        path: 'productAccess.status',
        Header: () => 'Status',
        minWidth: 30,
        ...defaults,
      },
      {
        path: 'productAccess.name',
        Header: () => 'Product',
        Cell: ({ value }: { value: string }) => Product.PRODUCT_DISPLAY_NAMES[value],
        minWidth: 42,
        ...defaults,
      },
      {
        id: 'productAccessId',
        path: 'productAccess.id',
        Header: () => 'ProductAccessId',
        minWidth: 62,
        ...defaults,
        resizable: false,
      },
      {
        path: 'productAccess.type',
        Header: () => 'License Type',
        minWidth: 42,
        ...defaults,
      },
      {
        path: 'productAccess.activityState',
        Header: () => 'Activity State',
        Cell: ({ value }: { value: string }) =>
          value === CostCenter.ACTIVITY_STATES.suspended ? (
            <i>{startCase(value)}</i>
          ) : (
            startCase(value)
          ),
        minWidth: 48,
        ...defaults,
      },
      {
        path: 'productAccess.startDate',
        Header: () => 'Start Date',
        Cell: DateTimeCell,
        sortMethod: compareDates,
        minWidth: 62,
        ...defaults,
      },
      {
        path: 'productAccess.endDate',
        Header: () => 'End Date',
        Cell: DateTimeCell,
        sortMethod: compareDates,
        minWidth: 62,
        ...defaults,
      },
      {
        path: 'costCenter.countryCode',
        Header: () => 'Country Code',
        minWidth: 40,
        ...defaults,
      },
      {
        path: 'productAccess.features',
        Header: () => 'Features',
        Cell: ({ value }: { value?: Record<string, LicenseFeature> }) => {
          if (!value || Object.keys(value).length === 0) return '—';
          const enabledFeatures = Object.values(value)
            .filter((feature) => feature.enabled)
            .map((feature) => feature.name)
            .join(', ');
          return enabledFeatures || '—';
        },
        minWidth: 76,
        ...defaults,
      },
    ];
  };

  const hasIncompleteSync = requestsCount > 0 ? currentRequest < requestsCount : syncFailed;
  const shouldRenderSyncPrompt =
    !dismissed &&
    syncFailed &&
    hasIncompleteSync &&
    (onRetrySync || onDismissSyncPrompt);
  const effectiveLoading =
    !dismissed &&
    (loading || (!syncFailed && requestsCount > 0 && currentRequest < requestsCount));
  const effectivePercentLoaded = percentLoaded ?? requestProgress ?? 100;
  const progressText =
    requestsCount > 0
      ? 'Sync was interrupted while loading license data. Restart sync?'
      : 'We could not finish syncing license data. Restart sync?';

  return (
    <TableContainer>
      <PaddedTable
        columns={getColumns()}
        data={licenses}
        selectedRowsByID={selectedRows}
        filterControls={filterControls}
        controls={controls}
        loading={effectiveLoading}
        percentLoaded={Math.min(Math.max(effectivePercentLoaded, 0), 100)}
        tableName="sa-individual-licenses"
        hidePagination={hidePagination}
        defaultPageSize={defaultPageSize}
      />
      {shouldRenderSyncPrompt && (
        <SyncFailedOverlay data-testid="individual-license-sync-failed">
          <SyncFailedContent>
            <p>{progressText}</p>
            {onRetrySync && <Button onClick={onRetrySync}>Restart Data Sync</Button>}
            {onDismissSyncPrompt && (
              <Button onClick={onDismissSyncPrompt}>Use Partially Loaded Data</Button>
            )}
          </SyncFailedContent>
        </SyncFailedOverlay>
      )}
    </TableContainer>
  );
};

export default IndividualLicenseTable;
